import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Typography, Stack, IconButton, Box, Tooltip } from '@mui/material'

import ChipStatus from 'packages/ui/ChipStatus'
import { getTimezoneByValue, formatTimezoneDisplay } from 'utils/timezones'
import CardActionButton from 'components/global/CardActions/CardActionButton'
import ActionExport from '../Card/ActionExport'
import { formatDate, formatTime, getEventStatus } from '../helpers'

export const Status = ({ item }) => {
  const { t } = useTranslation()
  
  const { isFinished, isSuspended, isActive, isUpcoming, hasError } = getEventStatus(item)

  let status, color, bgcolor
  
  if (hasError) {
    status = 'error'
    color = '#656565'
    bgcolor = '#EFEFEF'
  } else if (isFinished) {
    status = 'finished'
    color = '#DC2626'
    bgcolor = '#FEE2E2'
  } else if (isSuspended) {
    status = 'suspended'
    color = '#F59E0B'
    bgcolor = '#FEF3C7'
  } else if (isUpcoming) {
    status = 'upcoming'
    color = '#2570EB'
    bgcolor = '#DBE8FE'
  } else if (isActive) {
    status = 'active'
    color = '#587F31'
    bgcolor = '#E6F0D7'
  } else {
    status = 'unknown'
    color = '#656565'
    bgcolor = '#EFEFEF'
  }

  const chipContent = (
    <Box sx={{ height: '24px', display: 'inline-flex' }}>
      <ChipStatus
        status={t(`event_ssids.status.${status}`)}
        color={color}
        bgcolor={bgcolor}
        height="24px"
        borderRadius="50px"
      />
    </Box>
  )

  if (hasError) {
    return (
      <Tooltip title={item.error} arrow>
        {chipContent}
      </Tooltip>
    )
  }

  return chipContent
}

const PassphraseCell = ({ passphrase }) => {
  const [show, setShow] = useState(false)

  return (
    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ width: '200px', minWidth: '200px' }}>
      <Typography sx={{ fontSize: 12, color: '#656565', fontWeight: 400, letterSpacing: '2px' }}>
        {show ? passphrase : '********'}
      </Typography>
      <IconButton 
        size="small" 
        onClick={(e) => {
          e.stopPropagation()
          setShow(!show)
        }}
        sx={{ 
          border: '1px solid #EFEFEF',
          borderRadius: '8px',
          width: '24px',
          height: '24px',
          p: 0,
          minWidth: '24px',
          minHeight: '24px',
          flexShrink: 0
        }}
      >
        <i 
          className={`fa-light fa-${show ? 'eye-slash' : 'eye'}`} 
          style={{ fontSize: 12, color: '#656565' }} 
        />
      </IconButton>
    </Stack>
  )
}

const COLUMNS_CONFIG = [
  {
    name: 'status',
    header: {
      label: ctx => ctx.t('event_ssids.table.status')
    },
    value: item => <Status item={item} />
  },
  {
    name: 'label',
    header: {
      label: ctx => ctx.t('event_ssids.table.event_label')
    },
    value: item => (
      <Typography 
        component={'span'} 
        sx={{ 
          fontWeight: 600, 
          fontSize: 12, 
          color: '#656565',
          minWidth: '150px',
          display: 'block'
        }}
      >
        {item.label}
      </Typography>
    ),
    hideOnMobile: false
  },
  {
    name: 'ssid',
    header: {
      label: ctx => ctx.t('event_ssids.table.ssid')
    },
    value: item => (
      <Typography 
        component={'span'} 
        sx={{ 
          fontSize: 12, 
          color: '#656565',
          fontWeight: 400,
          minWidth: '150px',
          display: 'block'
        }}
      >
        {item.name}
      </Typography>
    ),
    hideOnMobile: false
  },
  {
    name: 'passphrase',
    header: {
      label: ctx => ctx.t('event_ssids.table.passphrase')
    },
    style: {
      width: 200,
      minWidth: 200
    },
    value: item => <PassphraseCell passphrase={item.passphrase} />,
    hideOnMobile: true
  },
  {
    name: 'start_date',
    header: {
      label: ctx => ctx.t('event_ssids.table.start_date')
    },
    value: item => (
      <Typography sx={{ fontSize: 12, color: '#656565', fontWeight: 400 }}>
        {formatDate(item.start_date)} - {formatTime(item.start_time)}
      </Typography>
    ),
    sortable: true,
    sortBy: 'start_tz',
    hideOnMobile: true
  },
  {
    name: 'end_date',
    header: {
      label: ctx => ctx.t('event_ssids.table.end_date')
    },
    value: item => (
      <Typography sx={{ fontSize: 12, color: '#656565', fontWeight: 400 }}>
        {formatDate(item.end_date)} - {formatTime(item.end_time)}
      </Typography>
    ),
    sortable: true,
    sortBy: 'end_tz',
    hideOnMobile: true
  },
  {
    name: 'utc_offset',
    header: {
      label: ctx => ctx.t('event_ssids.table.timezone')
    },
    value: (item, ctx) => {
      const timezone = getTimezoneByValue(item.utc_offset)
      return (
        <Typography sx={{ fontSize: 12, color: '#656565', fontWeight: 400 }}>
          {timezone ? formatTimezoneDisplay(timezone, ctx.t) : `UTC ${item.utc_offset}`}
        </Typography>
      )
    },
    hideOnMobile: true
  },
  {
    name: 'actions',
    header: {
      label: () => ''
    },
    style: {
      width: 100
    },
    value: (item, ctx) => {
      const { isFinished } = getEventStatus(item)

      return (
        <Stack direction="row" gap={0.5}>
          {!isFinished && (
            <>
              <Box onClick={(e) => e.stopPropagation()}>
                <ActionExport ssid={item} analyticsSource="wifi_management" />
              </Box>
              <CardActionButton
                iconlabel="pen"
                iconweight="light"
                iconsize="xs"
                tooltiptitle={ctx.t('event_ssids.actions.edit')}
                onClick={(e) => {
                  e.stopPropagation()
                  ctx.onEdit?.(item.id)
                }}
                style={{
                  border: '1px solid #EFEFEF',
                  borderRadius: '8px',
                  width: '24px',
                  height: '24px',
                  minWidth: '24px',
                  minHeight: '24px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#656565'
                }}
              />
            </>
          )}
        </Stack>
      )
    },
    hideOnMobile: false
  }
]

export default COLUMNS_CONFIG